// Camera-space hand: the equipped skin, a readable tool face and action-driven motion.
import * as THREE from 'three';
import { heldItemModel } from './craft-models.js';
import { ITEMS } from './crafting.js';
import { clamp, damp } from './util.js';
import { disposeObj } from './entities.js';

export class FirstPersonHand {
  constructor(character, mats) {
    this.character = character;
    this.mats = mats;
    this.root = new THREE.Group();
    this.grip = new THREE.Group();
    this.root.add(this.grip);
    // Own geometry/material; borrow the skin texture without ever disposing it.
    this.arm = new THREE.Mesh(character.armR.children[0].geometry.clone(), character.mat.clone());
    this.arm.material.userData.shared = false;
    this.arm.position.set(.04, -.21, .22);
    this.arm.rotation.set(-.9, 0, -.15);
    this.grip.add(this.arm);
    this.id = undefined;
    this.equip = 1;
    this.move = 0;
    this.swayX = 0;
    this.swayY = 0;
    this.time = 0;
    this.prepare(this.arm);
  }
  prepare(obj) {
    obj.traverse(o => {
      if (!o.material) return;
      for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
        m.depthTest = false;
        m.depthWrite = false;
      }
      o.renderOrder = 20;
      o.castShadow = o.receiveShadow = false;
    });
  }
  setItem(id) {
    if (this.id === id) return;
    if (this.item) { this.item.removeFromParent(); disposeObj(this.item); }
    this.id = id;
    this.item = id ? heldItemModel(id, this.mats) : null;
    if (this.item) {
      // World tools are edge-on in camera space; show their broad pixel-art face.
      this.item.rotation.set(.12, 0, -.18);
      if (ITEMS[id]?.kind === 'block') this.item.rotation.set(.15, -.45, .12);
      this.item.position.set(0, .05, -.06);
      this.grip.add(this.item);
      this.prepare(this.item);
    }
    this.equip = 0;
  }
  update(dt, camera, player, input, { first, id, shotAge = Infinity } = {}) {
    this.root.visible = first;
    if (!first) return;
    this.setItem(id);
    this.time += dt;
    this.equip = Math.min(1, this.equip + dt * 6);
    this.move = damp(this.move, player.onGround ? clamp(Math.hypot(player.vel.x, player.vel.z) / 6.4, 0, 1) : 0, 10, dt);
    this.swayX = damp(this.swayX, clamp((input.camDX || 0) * .001, -.045, .045), 12, dt);
    this.swayY = damp(this.swayY, clamp((input.camDY || 0) * .001, -.045, .045), 12, dt);
    const gait = this.character.walk;
    const swing = player.swingT >= 0 ? Math.sin(Math.PI * Math.sqrt(player.swingT)) : 0;
    const place = this.character.placeT > 0 ? Math.sin(Math.PI * this.character.placeT / .32) : 0;
    const recoil = shotAge < .3 ? Math.sin(shotAge / .3 * Math.PI) : 0;
    const guard = id === 'shield' && input.actionHeld ? 1 : 0;
    // Follow aspect ratio so a phone still sees the hand without covering the crosshair.
    const right = Math.min(.38, .8 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * camera.aspect * .72);
    this.grip.position.set(
      right + Math.sin(gait) * .018 * this.move + this.swayX - swing * .19 - guard * .15,
      -.29 + Math.abs(Math.cos(gait)) * .022 * this.move + Math.sin(this.time * 1.7) * .005 - this.swayY + swing * .12 + guard * .08 - (1 - this.equip) * .38,
      -.8 - swing * .13 - place * .2 + recoil * .12,
    );
    this.grip.rotation.set(-swing * .85 - place * .25 + recoil * .25, swing * .45, swing * .55 + Math.sin(gait) * .025 * this.move - guard * .2);
    this.grip.scale.setScalar(Math.min(.78, .45 + camera.aspect * .22));
    this.arm.material.map = this.character.mat.map;
    this.arm.material.emissiveMap = this.character.mat.emissiveMap;
    this.arm.material.color.copy(this.character.mat.color);
    this.root.position.copy(camera.position);
    this.root.quaternion.copy(camera.quaternion);
    this.root.updateMatrixWorld(true);
  }
  dispose() { this.root.removeFromParent(); disposeObj(this.root); }
}
